"use client";
import { useCallback, useEffect, useRef } from "react";
export const useDebouncedCallback = <T extends (...args: any[]) => any>(
  callback: T,
  deps: any[],
  delay: number,
): T & {
  cancel: () => void;
  flush: () => ReturnType<T> | undefined;
  immediate: T;
} => {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<any>(undefined);
  // The arguments of the call still waiting to run; cleared once it has run or was cancelled.
  const argsRef = useRef<any[]>(undefined);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    argsRef.current = undefined;
  };

  const flush = () => {
    const args = argsRef.current;
    cancel();
    if (args) {
      return callbackRef.current(...args);
    }
  };

  useEffect(() => {
    return () => {
      // Only a call that hasn't run yet; one the timer already ran must not run again.
      if (timeoutRef.current) flush();
    };
  }, []);

  // @ts-expect-error
  return Object.assign(
    useCallback((...args: Parameters<T>) => {
      argsRef.current = args;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = undefined;
        argsRef.current = undefined;
        callbackRef.current(...args);
      }, delay);
    }, deps),
    {
      flush,
      cancel,
      immediate: (...args: any[]) => {
        cancel();
        callbackRef.current(...args);
      },
    },
  );
};
